import { Injectable, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { vssServiceOptions } from '../../../mock-data/services-mock-data';

export interface MvssFormData {
  selectedService: string | null;
  totalMachines: number;
  includeNetworkDiscovery: boolean;
  advancedThreatDetection: boolean;
  extendedLogRetention: boolean;
  endpointControlEnabled: boolean;
  policyManagement: boolean;
  remoteAccess: boolean;
}

export interface MvssForm {
  selectedService: FormControl<string | null>;
  totalMachines: FormControl<number | null>;
  includeNetworkDiscovery: FormControl<boolean | null>;
  advancedThreatDetection: FormControl<boolean | null>;
  extendedLogRetention: FormControl<boolean | null>;
  endpointControlEnabled: FormControl<boolean | null>;
  policyManagement: FormControl<boolean | null>;
  remoteAccess: FormControl<boolean | null>;
}

@Injectable({
  providedIn: 'root',
})
export class MvssFormService {
  constructor(private fb: FormBuilder) {}

  createMvssForm(data?: MvssFormData): FormGroup<MvssForm> {
    const form = this.fb.group<MvssForm>({
      selectedService: this.fb.control<string | null>(data?.selectedService || null, [Validators.required]),
      totalMachines: this.fb.control<number | null>(data?.totalMachines || 0, [Validators.min(0)]),
      includeNetworkDiscovery: this.fb.control<boolean | null>(data?.includeNetworkDiscovery || false),
      advancedThreatDetection: this.fb.control<boolean | null>(data?.advancedThreatDetection || false),
      extendedLogRetention: this.fb.control<boolean | null>(data?.extendedLogRetention || false),
      endpointControlEnabled: this.fb.control<boolean | null>(data?.endpointControlEnabled || false),
      policyManagement: this.fb.control<boolean | null>(data?.policyManagement || false),
      remoteAccess: this.fb.control<boolean | null>(data?.remoteAccess || false),
    });

    if (data) {
      this.patchFormWithData(form, data);
    }

    return form;
  }

  private patchFormWithData(form: FormGroup<MvssForm>, data: MvssFormData): void {
    form.patchValue({
      selectedService: data.selectedService || null,
      totalMachines: data.totalMachines || 0,
      includeNetworkDiscovery: data.includeNetworkDiscovery || false,
      advancedThreatDetection: data.advancedThreatDetection || false,
      extendedLogRetention: data.extendedLogRetention || false,
      endpointControlEnabled: data.endpointControlEnabled || false,
      policyManagement: data.policyManagement || false,
      remoteAccess: data.remoteAccess || false,
    });

    // Apply conditional validation if service is already selected
    if (data.selectedService) {
      this.applyConditionalValidation(form, data.selectedService);
    }
  }

  selectService(form: FormGroup<MvssForm>, serviceValue: string): void {
    form.patchValue({ selectedService: serviceValue });
    this.applyConditionalValidation(form, serviceValue);
  }

  private applyConditionalValidation(form: FormGroup<MvssForm>, serviceValue: string): void {
    if (serviceValue === 'vss-essential') {
      // Essential VSS requires total machines
      form.get('totalMachines')?.setValidators([Validators.required, Validators.min(1)]);
    } else if (serviceValue === 'vss-enhanced') {
      // Enhanced VSS requires total machines and at least one advanced feature
      form.get('totalMachines')?.setValidators([Validators.required, Validators.min(1)]);
    }

    // Update validation
    form.get('totalMachines')?.updateValueAndValidity();
  }

  getFormData(form: FormGroup<MvssForm>): MvssFormData {
    const value = form.value;
    return {
      selectedService: value.selectedService || null,
      totalMachines: value.totalMachines || 0,
      includeNetworkDiscovery: value.includeNetworkDiscovery || false,
      advancedThreatDetection: value.advancedThreatDetection || false,
      extendedLogRetention: value.extendedLogRetention || false,
      endpointControlEnabled: value.endpointControlEnabled || false,
      policyManagement: value.policyManagement || false,
      remoteAccess: value.remoteAccess || false,
    };
  }

  onTotalMachinesChange(form: FormGroup<MvssForm>, value: number | null): void {
    form.patchValue({ totalMachines: value || 0 });
  }

  getServiceDescription(serviceValue: string): string {
    const service = this.getServiceOptions().find((s: any) => s.value === serviceValue);
    return service?.description || '';
  }

  getServiceOptions() {
    return vssServiceOptions;
  }

  debugFormState(form: FormGroup<MvssForm>): void {
    console.log('=== MVSS Form Debug ===');
    console.log('Form Valid:', form.valid);
    console.log('Form Invalid:', form.invalid);
    console.log('Form Pending:', form.pending);
    console.log('Form Dirty:', form.dirty);
    console.log('Form Touched:', form.touched);
    console.log('Form Errors:', form.errors);
    console.log('Selected Service:', form.get('selectedService')?.value);
    console.log('Selected Service Valid:', form.get('selectedService')?.valid);
    console.log('Selected Service Errors:', form.get('selectedService')?.errors);
    console.log('Total Machines:', form.get('totalMachines')?.value);
    console.log('Total Machines Valid:', form.get('totalMachines')?.valid);
    console.log('Total Machines Errors:', form.get('totalMachines')?.errors);

    // Log all form controls
    Object.keys(form.controls).forEach((key) => {
      const control = form.get(key);
      console.log(`${key}:`, {
        value: control?.value,
        valid: control?.valid,
        invalid: control?.invalid,
        errors: control?.errors,
        touched: control?.touched,
        dirty: control?.dirty,
      });
    });
  }
}
